import { faPlusCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useImperativeHandle, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { getData, saveData } from '../../../api/reportStore'
import { useSession } from '../../../context/SessionProvider'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { EmployeeCommutingFields, Transport } from '../../../interface/formFields.interface'
import getErrors from '../../../data/validation/employeeCommuting.validation'
import ModeOfTransportsInfo from '../../popups/ModeOfTransportsInfo'
import SectionHeader from '../../shared/SectionHeader'
import { useRegion } from '../../../context/RegionProvider'
import EmployeeCommutingInfoHeader from './components/EmployeeCommutingInfoHeader'
import TransportItem from './components/TransportItem'

const sectionName = 'EmployeeCommuting'

export default function EmployeeCommuting() {
  const { sessionId } = useSession()
  const navigate = useNavigate()
  const ref = useOutletContext<any>()
  const [errors, setErrors] = useState<string[]>([])
  const [showPopup, setShowPopup] = useState(false)
  const [sectionCompleted, setSectionCompleted] = useState<boolean>(false)
  const { country } = useRegion()

  const defaultTransport: Transport = {
    transportModeCode: 'NONE',
    value: 0,
    unitCode: country.defaultUnits.distance,
  }

  const openPopup = () => {
    setShowPopup(true)
  }

  const closePopup = () => {
    setShowPopup(false)
  }

  const { control, register, getValues, handleSubmit } = useForm<EmployeeCommutingFields>({
    defaultValues: {
      transports: [],
    },
  })

  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'transports',
  })

  //Called from the parent when navigating to a different route
  useImperativeHandle(ref, () => ({
    async save(params: { sectionCompleted: boolean }) {
      await save(getValues(), { sectionCompleted: params?.sectionCompleted })
    },
    async submit(navigationOverride: string) {
      await onSubmit(getValues(), navigationOverride)
    },
    sectionName,
  }))

  useEffect(() => {
    getData(sessionId).then((data: any) => {
      if (data['EmployeeCommuting']) {
        for (let idx = 0; idx < data['EmployeeCommuting'].length; idx++) {
          update(idx, data['EmployeeCommuting'][idx])
        }
      }
      if (data[sectionName + '_Status'] === 'Completed') {
        setSectionCompleted(true)
      }
    })
  }, [sessionId, update])

  async function save(values: EmployeeCommutingFields, params?: { sectionCompleted: boolean }) {
    const data: any = {
      [sectionName]: values.transports,
    }
    if (params?.sectionCompleted) {
      data[sectionName + '_Status'] = 'Completed'
    }
    await saveData(sessionId, data)
  }

  async function onSubmit(values: EmployeeCommutingFields, navigationOverride?: string) {
    const errors = getErrors(getValues())
    setErrors(errors)
    if (!errors.length) {
      await save(values, { sectionCompleted: true })
      navigate(navigationOverride ?? '/purchased-goods?sessionId=' + sessionId)
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  async function goBack() {
    const previousRoute = '/business-travel?sessionId=' + sessionId
    if (sectionCompleted) {
      await onSubmit(getValues(), previousRoute)
    } else {
      await save(getValues())
      navigate(previousRoute)
    }
  }

  return (
    <div className="formContainer">
      <SectionHeader title="Employee Commuting" errors={errors} />
      <EmployeeCommutingInfoHeader openPopup={openPopup} />
      {showPopup && <ModeOfTransportsInfo closePopup={closePopup} country={country} />}
      <form onSubmit={handleSubmit(async (values) => onSubmit(values))}>
        <div className="sectionBox">
          {!fields.length && (
            <div className="emptyPlaceholder">Add commuting transports or leave blank if it doesn't apply.</div>
          )}
          {fields.map((field, index) => (
            <TransportItem
              index={index}
              key={field.id}
              countryCode={country.code}
              remove={remove}
              register={register}
            />
          ))}
          <div className="addButton" data-cy="add-mode-of-transport-button" onClick={() => append(defaultTransport)}>
            <FontAwesomeIcon icon={faPlusCircle} className="gray6Color" /> Add mode of transport
          </div>
        </div>
        <div className="buttonContainer">
          <button type="button" onClick={goBack}>
            Back
          </button>
          <button type="submit">Next</button>
        </div>
      </form>
    </div>
  )
}
